import type { Circuit } from '../types/track.js';
import { START_LANE_LENGTH } from '../types/track.js';
import { areCellsAdjacent } from './circuitGeometryEdit.js';
import { rebuildGridFromPath } from './circuitEdit.js';
import { isCellOnMainPath } from './circuitJokerLapEdit.js';
import { applyStartLaneToGrid, createEmptyStartLane, createStartLaneCell } from './startLane.js';
import { applyJokerLapToGrid } from './jokerLap.js';

function isWithinGrid(circuit: Circuit, x: number, y: number): boolean {
  return x >= 0 && x < circuit.width && y >= 0 && y < circuit.height;
}

function isCellOnStartLane(circuit: Circuit, x: number, y: number): boolean {
  return (circuit.startLane?.cells ?? []).some((c) => c.x === x && c.y === y);
}

function isCellOnJokerLap(circuit: Circuit, x: number, y: number): boolean {
  return (circuit.jokerLap?.cells ?? []).some((c) => c.x === x && c.y === y);
}

export function ensureStartLaneDraft(circuit: Circuit): Circuit {
  if (!circuit.startLane) {
    return { ...circuit, startLane: createEmptyStartLane() };
  }
  return circuit;
}

function withUpdatedGrid(circuit: Circuit): Circuit {
  const withPathGrid = { ...circuit, grid: rebuildGridFromPath(circuit) };
  const withJoker = { ...withPathGrid, grid: applyJokerLapToGrid(withPathGrid) };
  return { ...withJoker, grid: applyStartLaneToGrid(withJoker) };
}

export function setStartLaneJoin(circuit: Circuit, pathIndex: number): Circuit {
  const next = ensureStartLaneDraft(circuit);
  return withUpdatedGrid({
    ...next,
    startLane: {
      joinPathIndex: pathIndex,
      cells: [],
    },
  });
}

export function clearStartLane(circuit: Circuit): Circuit {
  const { startLane: _removed, ...rest } = circuit;
  return rest;
}

export function tryAppendStartLaneCell(
  circuit: Circuit,
  x: number,
  y: number
): { circuit: Circuit } | { error: string } {
  const sl = circuit.startLane;
  if (!sl || sl.joinPathIndex < 0) {
    return { error: 'Choisissez d’abord le point de jonction sur le circuit principal' };
  }
  if (sl.cells.length >= START_LANE_LENGTH) {
    return { error: `La ligne de départ est limitée à ${START_LANE_LENGTH} cases` };
  }
  if (!isWithinGrid(circuit, x, y)) {
    return { error: 'Position hors de la grille' };
  }
  if (isCellOnMainPath(circuit, x, y)) {
    return { error: 'Cette case est déjà occupée par le circuit principal' };
  }
  if (isCellOnJokerLap(circuit, x, y)) {
    return { error: 'Cette case est déjà occupée par le tour joker' };
  }
  if (isCellOnStartLane(circuit, x, y)) {
    return { error: 'Case déjà sur la ligne de départ' };
  }

  const join = circuit.path[sl.joinPathIndex];
  const cells = sl.cells;

  if (cells.length === 0) {
    if (!areCellsAdjacent(join.x, join.y, x, y)) {
      return { error: 'La première case doit être adjacente au point de jonction' };
    }
  } else {
    const last = cells[cells.length - 1];
    if (!areCellsAdjacent(last.x, last.y, x, y)) {
      return { error: 'La case doit être adjacente à la précédente' };
    }
    if (cells.length >= 2) {
      const dx = cells[1].x - cells[0].x;
      const dy = cells[1].y - cells[0].y;
      const stepX = x - last.x;
      const stepY = y - last.y;
      if (stepX !== dx || stepY !== dy) {
        return { error: 'La ligne de départ doit rester droite' };
      }
    } else {
      const dx = cells[0].x - join.x;
      const dy = cells[0].y - join.y;
      const stepX = x - last.x;
      const stepY = y - last.y;
      if (stepX !== dx || stepY !== dy) {
        return { error: 'Prolongez la ligne de départ dans le même axe' };
      }
    }
  }

  const newIndex = cells.length;
  const isBackOfGrid = newIndex === START_LANE_LENGTH - 1;
  const nextCells = [...cells, createStartLaneCell(x, y, isBackOfGrid)];
  const withLane = {
    ...circuit,
    startLane: { ...sl, cells: nextCells },
  };
  return { circuit: withUpdatedGrid(withLane) };
}

export function undoLastStartLaneCell(circuit: Circuit): Circuit | null {
  const sl = circuit.startLane;
  if (!sl || sl.cells.length === 0) return null;
  const withLane = {
    ...circuit,
    startLane: { ...sl, cells: sl.cells.slice(0, -1) },
  };
  return withUpdatedGrid(withLane);
}

/**
 * Ne dépend PAS de la fermeture de la boucle : la ligne de départ peut se
 * tracer dès que le point de jonction existe (typiquement juste après la case
 * de départ elle-même, avant même de tracer le reste du circuit — voir
 * `circuit3dEditor.ts`, qui trace désormais la ligne de départ EN PREMIER).
 * `validateStartLane` (voir `startLane.ts`) ne dépend pas non plus de la
 * fermeture : seul `joinPathIndex < circuit.path.length` est requis.
 */
export function getStartLanePlaceableCells(circuit: Circuit): { x: number; y: number }[] {
  const sl = circuit.startLane;
  if (!sl || sl.joinPathIndex < 0) return [];
  if (sl.cells.length >= START_LANE_LENGTH) return [];

  const join = circuit.path[sl.joinPathIndex];
  const anchor = sl.cells.length === 0 ? join : sl.cells[sl.cells.length - 1];

  const neighbors = [
    { x: anchor.x + 1, y: anchor.y },
    { x: anchor.x - 1, y: anchor.y },
    { x: anchor.x, y: anchor.y + 1 },
    { x: anchor.x, y: anchor.y - 1 },
  ];

  return neighbors.filter((cell) => {
    if (!isWithinGrid(circuit, cell.x, cell.y)) return false;
    if (isCellOnMainPath(circuit, cell.x, cell.y)) return false;
    if (isCellOnJokerLap(circuit, cell.x, cell.y)) return false;
    if (isCellOnStartLane(circuit, cell.x, cell.y)) return false;
    if (sl.cells.length === 0) {
      return areCellsAdjacent(join.x, join.y, cell.x, cell.y);
    }
    const last = sl.cells[sl.cells.length - 1];
    if (!areCellsAdjacent(last.x, last.y, cell.x, cell.y)) return false;
    if (sl.cells.length >= 2) {
      const dx = sl.cells[1].x - sl.cells[0].x;
      const dy = sl.cells[1].y - sl.cells[0].y;
      return cell.x - last.x === dx && cell.y - last.y === dy;
    }
    const dx = sl.cells[0].x - join.x;
    const dy = sl.cells[0].y - join.y;
    return cell.x - last.x === dx && cell.y - last.y === dy;
  });
}
